import { useCallback, useEffect, useState } from 'react';
import { Hand } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useAuth, hasRole } from '../lib/auth';

/**
 * Botón "Working on it" (time tracking) auto-contenido: se puede soltar en cualquier claim.
 * Consulta quién trabaja en `taskType`; sin nadie → botón para tomarlo; con alguien → nota
 * amarilla con quién y desde cuándo (para que nadie duplique) + Stop (mío o super-manager).
 * Mismo backend que el estimado (/api/claims/:id/work/:taskType/start|stop).
 */
type ActiveWork = { task_type: string; user_id: string; user_name: string | null; started_at: string };

const minutesSince = (iso: string) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));

export function WorkOnItButton({ claimId, taskType, label }: { claimId: string; taskType: string; label: string }) {
  const { t } = useI18n();
  const { me } = useAuth();
  const myId = me?.kind === 'tenant' ? me.user.id : null;
  const [active, setActive] = useState<ActiveWork | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<{ active: ActiveWork[] }>(`/api/claims/${claimId}/work`)
      .then((r) => setActive(r.active.find((a) => a.task_type === taskType) ?? null))
      .catch(() => { /* ignore */ });
  }, [claimId, taskType]);
  useEffect(() => { load(); }, [load]);

  const isMine = active && String(active.user_id) === String(myId);
  const canForceStop = hasRole(me, 'super-manager');

  async function start() {
    setMsg(null);
    try { await api.post(`/api/claims/${claimId}/work/${taskType}/start`, {}); load(); }
    catch (e) { setMsg(e instanceof ApiError && e.code === 'already_in_progress' ? t('work.taken') : (e instanceof ApiError ? e.message : 'Error')); load(); }
  }
  async function stop() {
    const reason = window.prompt(t('work.stopReason'));
    if (reason === null) return;
    try { await api.post(`/api/claims/${claimId}/work/${taskType}/stop`, { reason }); load(); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); }
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {!active ? (
        <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={start}><Hand size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('work.start')}: {label}</button>
      ) : (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--orange-soft)', border: '1px solid var(--orange)', borderRadius: 8, padding: '2px 8px', fontSize: 'var(--fs-2xs)', color: 'var(--orange)' }}>
          <span><b>{label}</b> · <span className="dot orange" style={{ marginRight: 2 }} /> {active.user_name || '—'} · {t('work.since', { m: String(minutesSince(active.started_at)) })}</span>
          {(isMine || canForceStop) && <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)', padding: '1px 6px' }} onClick={stop}>{t('work.stop')}</button>}
        </span>
      )}
      {msg && <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--red)' }}>{msg}</span>}
    </span>
  );
}
